import React, { useState, useEffect } from 'react';
import { MdAddCircleOutline, MdDeleteOutline } from 'react-icons/md';

// ─── helpers ────────────────────────────────────────────────────────────────

export function parseTaskText(text = '') {
    return text
        .split('\n')
        .filter((line) => line.trim() !== '')
        .map((line, idx) => {
            if (line.match(/^\[x\]\s/i)) return { id: idx, checked: true,  label: line.slice(4) };
            if (line.match(/^\[ \]\s/))  return { id: idx, checked: false, label: line.slice(4) };
            return { id: idx, checked: false, label: line };  // legacy plain lines
        });
}

export function serializeTasksToText(tasks = []) {
    return tasks.map((t) => `${t.checked ? '[x]' : '[ ]'} ${t.label}`).join('\n');
}

// Hover effects for delete button (matches app's existing hover patterns)
const hoverCss = `
.issues-task-row:hover .issues-delete-btn { opacity: 1 !important; }
.issues-delete-btn:hover { color: #c0392b !important; }
`;

// ─── component ───────────────────────────────────────────────────────────────

export default function IssuesChecklist({ value = '', onChange, label = 'Issues Faced' }) {
    const [tasks,   setTasks]   = useState(() => parseTaskText(value));
    const [newItem, setNewItem] = useState('');

    useEffect(() => { setTasks(parseTaskText(value)); }, [value]); // eslint-disable-line

    const emit = (updated) => { setTasks(updated); onChange(serializeTasksToText(updated)); };

    const toggleCheck = (idx) => emit(tasks.map((t, i) => i === idx ? { ...t, checked: !t.checked } : t));
    const updateLabel = (idx, val) => emit(tasks.map((t, i) => i === idx ? { ...t, label: val } : t));
    const removeTask  = (idx) => emit(tasks.filter((_, i) => i !== idx));

    const addTask = () => {
        const trimmed = newItem.trim();
        if (!trimmed) return;
        emit([...tasks, { id: Date.now(), checked: false, label: trimmed }]);
        setNewItem('');
    };

    return (
        <div style={{ width: '100%', marginTop: '10px' }}>
            <style>{hoverCss}</style>

            {/* Label — same class as the original "Issues Faced:" span */}
            <span className="information_light">{label}: </span>

            {/* Task rows — font/size matches MUI TextField size="small" */}
            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {tasks.length === 0 && (
                    <span style={{ fontSize: '0.875rem', color: '#aaa', padding: '4px 8px' }}>
                        No issues logged yet
                    </span>
                )}

                {tasks.map((task, idx) => (
                    <div
                        key={task.id ?? idx}
                        className="information_light"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: task.checked ? 'rgba(90,158,111,0.08)' : 'transparent',
                            transition: 'background 0.15s',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={task.checked}
                            onChange={() => toggleCheck(idx)}
                            style={{
                                accentColor: '#3a7d44',
                                width: '13px',
                                height: '13px',
                                flexShrink: 0,
                                cursor: 'pointer',
                                margin: 0,
                            }}
                        />

                        <input
                            type="text"
                            value={task.label}
                            onChange={(e) => updateLabel(idx, e.target.value)}
                            style={{
                                flex: 1,
                                fontSize: '0.875rem',     // MUI TextField size="small"
                                fontFamily: 'inherit',
                                fontWeight: 'inherit',
                                lineHeight: '1.4375em',   // MUI small line-height
                                color: task.checked ? '#aaa' : 'inherit',
                                textDecoration: task.checked ? 'line-through' : 'none',
                                background: 'transparent',
                                border: 'none',
                                outline: 'none',
                                padding: 0,
                                width: '100%',
                                cursor: 'text',
                            }}
                        />

                        <button
                            className="issues-delete-btn"
                            onClick={() => removeTask(idx)}
                            title="Remove"
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#bbb',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px',
                                flexShrink: 0,
                                opacity: 0,
                                transition: 'color 0.15s',
                            }}
                        >
                            <MdDeleteOutline size={15} />
                        </button>
                    </div>
                ))}
            </div>

            {/* Add row — styled like a small underlined input, same as the form's other fields */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: '8px',
                paddingTop: '6px',
                borderTop: '1px dashed #ccc',
            }}>
                <input
                    type="text"
                    placeholder="preferred format: row number/range - problem"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTask(); } }}
                    style={{
                        flex: 1,
                        fontSize: '0.875rem',
                        fontFamily: 'inherit',
                        lineHeight: '1.4375em',
                        border: 'none',
                        borderBottom: '1px solid rgba(0,0,0,0.42)',  // MUI standard underline
                        outline: 'none',
                        background: 'transparent',
                        padding: '2px 0',
                        color: 'inherit',
                    }}
                />
                <button
                    onClick={addTask}
                    disabled={!newItem.trim()}
                    title="Add (Enter)"
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: newItem.trim() ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '2px',
                        flexShrink: 0,
                    }}
                >
                    <MdAddCircleOutline size={18} color={newItem.trim() ? '#3a7d44' : '#ccc'} />
                </button>
            </div>
        </div>
    );
}